'use client'

import { Suspense, use, type ReactNode } from 'react'
import type * as React from 'react'
import { Tooltip as TooltipPrimitive } from '@base-ui/react/tooltip'

import { useHydrated } from '@/hooks/use-hydrated'
import { useIsHydratingRender } from '@/hooks/use-hydrating-render'
import { cn } from '@/lib/utils'
import { GatedTooltipTrigger, TooltipSsrGateProvider, useTooltipSsrGate } from './tooltip-ssr-gate'

// Module-scope aliases, never `TooltipPrimitive.X` inside a component: the React Compiler hoists a namespace it reads there into
// a memo temp, which materializes it, and a materialized namespace keeps EVERY part in its chunk — the lazy popup too.
const ProviderPart = TooltipPrimitive.Provider
const RootPart = TooltipPrimitive.Root
const TriggerPart = TooltipPrimitive.Trigger
const PortalPart = TooltipPrimitive.Portal

function TooltipProvider({ delay = 0, ...props }: TooltipPrimitive.Provider.Props) {
  return <ProviderPart data-slot="tooltip-provider" delay={delay} {...props} />
}

function Tooltip({ ...props }: TooltipPrimitive.Root.Props) {
  /**
   * On the server — and on the client render that hydrates it — the whole Base UI tooltip is replaced by the one
   * element it would have emitted; the real one mounts in the re-render straight after hydration. A tooltip asked
   * to start OPEN has real popup content in the server HTML, so it is never gated.
   * Both flags, and neither is redundant — what each one is for: docs/notes/app/tooltip-ssr-gate.md
   */
  const hydrating = useIsHydratingRender()
  const hydrated = useHydrated()
  // A render-function child is Base UI's payload API — it needs the real root to be called at all.
  const gateable = !props.open && !props.defaultOpen && typeof props.children !== 'function'
  if ((hydrating || !hydrated) && gateable) {
    return <TooltipSsrGateProvider value={true}>{props.children as ReactNode}</TooltipSsrGateProvider>
  }

  return <RootPart data-slot="tooltip" {...props} />
}

/** Intent handlers for a trigger: the hover or focus that will open the tooltip fetches its popup first. */
function withPreload<P extends { onPointerEnter?: unknown; onFocus?: unknown }>(props: P): P {
  const { onPointerEnter, onFocus } = props as {
    onPointerEnter?: (event: React.PointerEvent<HTMLElement>) => void
    onFocus?: (event: React.FocusEvent<HTMLElement>) => void
  }
  return {
    ...props,
    onPointerEnter: (event: React.PointerEvent<HTMLElement>) => {
      void preloadTooltipPopup()
      onPointerEnter?.(event)
    },
    onFocus: (event: React.FocusEvent<HTMLElement>) => {
      void preloadTooltipPopup()
      onFocus?.(event)
    },
  }
}

function TooltipTrigger({ ...props }: TooltipPrimitive.Trigger.Props) {
  if (useTooltipSsrGate()) return <GatedTooltipTrigger {...props} />

  return <TriggerPart data-slot="tooltip-trigger" {...withPreload(props)} />
}

type PopupModule = typeof import('./tooltip-popup')
type TrackedPopupPromise = Promise<PopupModule> & { status?: 'fulfilled'; value?: PopupModule }
let popupPromise: TrackedPopupPromise | null = null

/**
 * Once loaded, the promise is stamped fulfilled the way React stamps a thenable it tracks, so `use()` returns it
 * synchronously — even when the hover preloaded it before React ever saw it. `use()` must run on EVERY render:
 * skipping it after a render that suspended is a conditional `use()`, which React 19's dev build reports.
 */
export function preloadTooltipPopup(): Promise<PopupModule> {
  if (popupPromise) return popupPromise
  const pending: TrackedPopupPromise = import('./tooltip-popup').then((module) => {
    Object.assign(pending, { status: 'fulfilled', value: module })
    return module
  })
  popupPromise = pending
  return pending
}

function LazyTooltipPopup(props: React.ComponentProps<PopupModule['TooltipPositionedPopup']>) {
  const Popup = use(preloadTooltipPopup()).TooltipPositionedPopup
  return <Popup {...props} />
}

function LazyTooltipViewport(props: React.ComponentProps<PopupModule['TooltipViewport']>) {
  const Viewport = use(preloadTooltipPopup()).TooltipViewport
  return <Viewport {...props} />
}

function TooltipContent(props: React.ComponentProps<PopupModule['TooltipPositionedPopup']>) {
  // Portalled, so it emits nothing on the server anyway — but under the gate there is no Base UI root to
  // read from, and rendering it would throw.
  if (useTooltipSsrGate()) return null

  // The Portal renders its children only while the tooltip is open, so a tooltip never hovered never fetches its popup.
  // No fallback: a tooltip appearing a beat later is no dead control, and the trigger's hover already fetched it.
  return (
    <PortalPart>
      <Suspense fallback={null}>
        <LazyTooltipPopup {...props} />
      </Suspense>
    </PortalPart>
  )
}

/**
 * Base UI's `adaptiveOrigin` middleware only anchors the positioner with physical offsets — the thing that
 * lets it SLIDE to the next trigger instead of jumping — when the element already computes a non-zero
 * transition-duration, so this class is load-bearing rather than decorative (utils/adaptiveOriginMiddleware).
 * Travel and box-morph share ONE curve so a swap between stacked rows reads as a single movement rather than a
 * slide racing a resize; 220ms on height because that is the distance the eye actually tracks vertically.
 * Written out rather than interpolated from a constant: Tailwind scans for literal class strings.
 */
const GROUP_POSITIONER_CLASSES =
  'transition-[top,right,bottom,left] duration-200 [transition-timing-function:cubic-bezier(0.32,0.72,0,1)] motion-reduce:transition-none'

/**
 * `--popup-width`/`--popup-height` are the measured size Base UI publishes per swap; at rest they are `auto`,
 * and `interpolate-size` is what keeps the px→auto handoff at the end of a swap from snapping the height.
 * Each axis animates only the dimension the eye is already following and snaps the other, so the text stops
 * rewrapping under a morph it is not watching: travelling down a rail that is height, along a row it is width.
 * The duration goes through `duration-*`, never an arbitrary `[transition-duration:…]`: Tailwind emits
 * `transition-duration: var(--tw-duration, 0s)`, which outran the longhand and measured 0s on the swap.
 */
const GROUP_POPUP_CLASSES = [
  'h-(--popup-height) w-(--popup-width) [interpolate-size:allow-keywords]',
  '[transition-property:width,height] ease-[cubic-bezier(0.32,0.72,0,1)] duration-200',
  'has-[[data-activation-direction*=up]]:[transition-property:height] has-[[data-activation-direction*=up]]:duration-[220ms]',
  'has-[[data-activation-direction*=down]]:[transition-property:height] has-[[data-activation-direction*=down]]:duration-[220ms]',
  // Mutually exclusive with the vertical arms by construction: Base UI writes "<left|right|''> <up|down|''>",
  // so a horizontal-only move leaves the vertical half empty and the value ends in a space (utils/usePopupViewport).
  "has-[[data-activation-direction$='_']]:[transition-property:width] has-[[data-activation-direction$='_']]:duration-[150ms]",
  'motion-reduce:transition-none',
].join(' ')

// The viewport carries `data-activation-direction` ("<left|right|''> <up|down|''>"), so the incoming text
// enters from the side the pointer came FROM and the outgoing copy leaves the other way.
const GROUP_VIEWPORT_CLASSES = [
  'relative overflow-hidden',
  '*:transition-[opacity,translate] *:duration-150 *:[transition-timing-function:cubic-bezier(0.32,0.72,0,1)] motion-reduce:*:transition-none',
  '[&>[data-previous]]:h-(--popup-height) [&>[data-previous]]:w-(--popup-width)',
  '[&>[data-current][data-starting-style]]:opacity-0 [&>[data-previous][data-ending-style]]:opacity-0',
  '[&[data-activation-direction*=left]>[data-current][data-starting-style]]:-translate-x-2',
  '[&[data-activation-direction*=left]>[data-previous][data-ending-style]]:translate-x-2',
  '[&[data-activation-direction*=right]>[data-current][data-starting-style]]:translate-x-2',
  '[&[data-activation-direction*=right]>[data-previous][data-ending-style]]:-translate-x-2',
  '[&[data-activation-direction*=up]>[data-current][data-starting-style]]:-translate-y-2',
  '[&[data-activation-direction*=up]>[data-previous][data-ending-style]]:translate-y-2',
  '[&[data-activation-direction*=down]>[data-current][data-starting-style]]:translate-y-2',
  '[&[data-activation-direction*=down]>[data-previous][data-ending-style]]:-translate-y-2',
].join(' ')

/**
 * ONE popup for a row of siblings. Each trigger hands its own content in as Base UI's `payload`, so moving
 * along the row swaps what the popup SAYS instead of unmounting one tooltip and mounting the next — no
 * re-delay and no re-played open animation between neighbours. `Tooltip.Viewport` animates that swap.
 * Gated exactly as `Tooltip` is: under the gate there is no root at all, and the triggers emit themselves.
 */
function TooltipGroup({
  children,
  side = 'top',
  sideOffset,
  align,
  alignOffset,
  className,
}: {
  children: ReactNode
} & Pick<TooltipPrimitive.Positioner.Props, 'align' | 'alignOffset' | 'side' | 'sideOffset'> & {
    className?: string
  }) {
  const hydrating = useIsHydratingRender()
  const hydrated = useHydrated()
  if (hydrating || !hydrated) {
    return <TooltipSsrGateProvider value={true}>{children}</TooltipSsrGateProvider>
  }

  return (
    <RootPart<ReactNode>>
      {({ payload }) => (
        <>
          {children}
          <TooltipContent
            side={side}
            sideOffset={sideOffset}
            align={align}
            alignOffset={alignOffset}
            positionerClassName={GROUP_POSITIONER_CLASSES}
            className={cn(GROUP_POPUP_CLASSES, className)}
          >
            <LazyTooltipViewport className={GROUP_VIEWPORT_CLASSES}>{payload}</LazyTooltipViewport>
          </TooltipContent>
        </>
      )}
    </RootPart>
  )
}

/**
 * A trigger inside a `TooltipGroup`: its `tip` is what the shared popup shows while hovered. Named `tip` and
 * not `content`, which is a real HTML attribute on the element and typed as a string. `closeDelay` is what
 * carries the popup across the GAP between two siblings — leaving a trigger closes it at once by default, so
 * 2px of padding was enough to shut it and make the neighbour re-pay the 600ms open delay.
 */
const GROUP_CLOSE_DELAY = 150

function TooltipGroupTrigger({ tip, closeDelay, ...props }: TooltipPrimitive.Trigger.Props & { tip: ReactNode }) {
  if (useTooltipSsrGate()) return <GatedTooltipTrigger {...props} />

  return (
    <TriggerPart
      data-slot="tooltip-trigger"
      payload={tip}
      closeDelay={closeDelay ?? GROUP_CLOSE_DELAY}
      {...withPreload(props)}
    />
  )
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, TooltipGroup, TooltipGroupTrigger }
